import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin, Search, X, Hash, Mailbox } from "lucide-react";
import { api, errMsg } from "../../lib/api";

/** Détecte le type de recherche : code postal (5 chiffres), n° de tournée
 * (1 à 4 chiffres) ou adresse (tout le reste, avec autocomplétion). */
export function detectMode(q) {
  const s = q.trim();
  if (/^\d{5}$/.test(s)) return "cp";
  if (/^\d{1,4}$/.test(s)) return "tournee";
  return "adresse";
}

export function SearchBox({ onAddress, onCp, onTournee, onClear, tourneeCodes }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(0);
  const reqId = useRef(0);
  const boxRef = useRef(null);
  const chosen = useRef(null); // libellé choisi : ne relance pas l'autocomplétion

  const mode = detectMode(q);

  useEffect(() => {
    setError("");
    if (mode !== "adresse" || q.trim().length < 3) { setItems([]); return; }
    if (chosen.current === q) return;
    const id = ++reqId.current;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geo/adresse", { params: { q: q.trim(), limit: 6 } });
        if (id === reqId.current) { setItems(data); setActive(0); setOpen(true); }
      } catch (e) {
        if (id === reqId.current) { setItems([]); setError(errMsg(e)); }
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q, mode]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (it) => {
    chosen.current = it.label;
    setQ(it.label);
    setOpen(false);
    setItems([]);
    reqId.current++;
    onAddress(it);
  };

  const submit = () => {
    const s = q.trim();
    if (!s) return;
    if (mode === "cp") { setOpen(false); onCp(s); }
    else if (mode === "tournee") { setOpen(false); onTournee(s); }
    else if (items[active]) choose(items[active]);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); setOpen(true); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); submit(); }
    else if (e.key === "Escape") setOpen(false);
  };

  const hint =
    mode === "cp" ? { icon: Mailbox, text: `Code postal ${q.trim()} — Entrée pour afficher` }
    : mode === "tournee" ? { icon: Hash, text: tourneeCodes?.includes(q.trim()) ? `Tournée ${q.trim()} — Entrée pour afficher` : `Tournée ${q.trim()} (inconnue)` }
    : null;

  return (
    <div ref={boxRef} className="relative">
      <div className="flex items-center gap-2 h-12 px-3 rounded-xl bg-card border border-border shadow-lg focus-within:ring-2 focus-within:ring-ring">
        <Search size={18} className="text-muted-foreground shrink-0" />
        <input
          value={q}
          onChange={(e) => { chosen.current = null; setQ(e.target.value); setOpen(true); }}
          onFocus={() => { if (chosen.current !== q) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="Adresse, code postal ou n° de tournée"
          className="flex-1 min-w-0 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground"
          data-testid="search-input"
          aria-label="Rechercher"
          autoComplete="off"
        />
        {loading && <Loader2 size={16} className="animate-spin text-muted-foreground" />}
        {q && (
          <button
            onClick={() => { setQ(""); setItems([]); setError(""); onClear?.(); }}
            className="p-1 rounded-md hover:bg-accent text-muted-foreground"
            aria-label="Effacer"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {open && (hint || items.length > 0 || error) && (
        <div className="absolute left-0 right-0 mt-2 rounded-xl bg-popover border border-border shadow-2xl overflow-hidden z-20" data-testid="search-suggestions">
          {hint && (
            <button onClick={submit} className="w-full flex items-center gap-3 px-3 py-3 text-left text-sm hover:bg-accent">
              <hint.icon size={16} className="text-primary" /> {hint.text}
            </button>
          )}
          {items.map((it, i) => (
            <button
              key={`${it.label}-${i}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(it)}
              className={`w-full flex items-start gap-3 px-3 py-2.5 text-left ${i === active ? "bg-accent" : ""}`}
            >
              <MapPin size={16} className="mt-0.5 text-muted-foreground shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{it.name || it.label}</span>
                <span className="block text-xs text-muted-foreground truncate">{it.postcode} {it.city}{it.context ? ` · ${it.context}` : ""}</span>
              </span>
            </button>
          ))}
          {error && <p className="px-3 py-3 text-sm text-destructive">{error}</p>}
        </div>
      )}
    </div>
  );
}
